
import Vue from 'vue';
import { mapGetters } from 'vuex';
import pluralize from 'pluralize';
import { CompanyUserRoleDef, Team, TeamUserRoleDef } from '@run-diffusion/shared';
import BaseButton from '@/components/base/BaseButton.vue';
import ClipboardCopy from '@/components/ClipboardCopy.vue';
import RoleDefBadge from '@/views/Team/CompanyDashboard/RoleDefBadge.vue';
import {
	ScimGroupRow,
	TeamMappingDraft,
	availableTeamsForMapping,
	canAddTeamMapping,
	companyRoleEmailsWithoutTeamMapping,
	getMemberEmailsList,
	getRowAssignmentType,
	isRowUnmapped,
	sortedTeamMappings,
	teamsWithNoGroupInPlan,
} from './scimPlan';

/** Emails named on the row before the note switches to a count, to keep it to a line or two. */
const BLOCKING_EMAILS_NAMED_ON_ROW = 3;

/**
 * Stage 02: the plan. Editing here never moves anyone, which is the whole point of splitting the
 * flow, so this component only edits drafts and emits. The page owns the state and the writes.
 */
export default Vue.extend({
	name: 'ScimPlanStep',
	components: {
		BaseButton,
		ClipboardCopy,
		RoleDefBadge,
	},
	props: {
		rows: { type: Array as () => ScimGroupRow[], default: () => [] },
		teamsSorted: { type: Array as () => Team[], default: () => [] },
		teamUserRoleDefs: { type: Array as () => TeamUserRoleDef[], default: () => [] },
		companyUserRoleDefs: { type: Array as () => CompanyUserRoleDef[], default: () => [] },
		loadingGroups: { type: Boolean, default: false },
		loadingTeams: { type: Boolean, default: false },
		/**
		 * The team list could not be loaded in full. `teamsSorted` is then incomplete or empty, so the
		 * "no group provisions this" section cannot be trusted and has to say so instead of showing
		 * nothing, which would read as "no team is at risk".
		 */
		teamsLoadFailed: { type: Boolean, default: false },
		loadingTeamRoleDefs: { type: Boolean, default: false },
		loadingCompanyRoleDefs: { type: Boolean, default: false },
		dirty: { type: Boolean, default: false },
		saving: { type: Boolean, default: false },
		/** True while a dry run or deploy is in flight: the plan must not move underneath it. */
		disabled: { type: Boolean, default: false },
	},
	computed: {
		...mapGetters([
			'displayLabels',
		]),
		assignmentTypeItems (): Array<{ text: string; value: 'team' | 'company' }> {
			return [
				{ text: this.displayLabels.team, value: 'team' },
				{ text: 'Company', value: 'company' },
			];
		},
		unmappedTeams (): Team[] {
			return teamsWithNoGroupInPlan(this.rows, this.teamsSorted);
		},
		/**
		 * Computed once for the whole plan rather than per row: the answer depends on every other
		 * group's members, so a row cannot decide it alone.
		 */
		emailsWithoutTeamMapping (): Set<string> {
			return new Set(companyRoleEmailsWithoutTeamMapping(this.rows));
		},
		planSummary (): string {
			const groups = `${this.rows.length} ${pluralize('group', this.rows.length)}`;
			const unmappedGroups = this.rows.filter((row: ScimGroupRow) => isRowUnmapped(row)).length;
			const parts = [groups];
			if (unmappedGroups > 0) parts.push(`${unmappedGroups} unmapped`);
			const blocking = this.emailsWithoutTeamMapping.size;
			if (blocking > 0) {
				parts.push(`${blocking} ${pluralize('member', blocking)} with no ${this.displayLabels.team}`);
			}
			if (this.teamsLoadFailed) {
				parts.push(`${pluralize(this.displayLabels.team)} not loaded`);
			} else if (this.unmappedTeams.length > 0) {
				parts.push(`${this.unmappedTeams.length} ${pluralize(this.displayLabels.team, this.unmappedTeams.length)} with no group`);
			}
			return parts.join(' · ');
		},
	},
	methods: {
		isRowUnmapped,
		assignmentTypeFor (row: ScimGroupRow): 'team' | 'company' {
			return getRowAssignmentType(row);
		},
		teamMappingsFor (row: ScimGroupRow): TeamMappingDraft[] {
			return sortedTeamMappings(row, this.teamsSorted);
		},
		teamsFor (row: ScimGroupRow, mapping: TeamMappingDraft): Team[] {
			return availableTeamsForMapping(row, mapping, this.teamsSorted);
		},
		canAddTeam (row: ScimGroupRow): boolean {
			return canAddTeamMapping(row, this.teamsSorted);
		},
		memberCount (row: ScimGroupRow): number {
			return getMemberEmailsList(row).length;
		},
		/** This row's members that the deploy would block on, so the note names the group at fault. */
		membersWithoutTeamMapping (row: ScimGroupRow): string[] {
			return getMemberEmailsList(row).filter((email: string) => this.emailsWithoutTeamMapping.has(email));
		},
		membersWithoutTeamMappingLabel (row: ScimGroupRow): string {
			const emails = this.membersWithoutTeamMapping(row);
			const named = emails.slice(0, BLOCKING_EMAILS_NAMED_ON_ROW);
			const unnamed = emails.length - named.length;
			const list = unnamed > 0 ? `${named.join(', ')} and ${unnamed} more` : named.join(', ');
			// Reads as one sentence with the template's "in no group mapped to a <Team>".
			return emails.length === 1 ? `${list} is` : `${emails.length} members (${list}) are`;
		},
	},
});
